import { MouseEventHandler } from 'react';
import { LinkConfig } from 'types/WebMicrofrontends';
import { Button } from 'components/01-atoms/Button';
import { Icon } from 'components/01-atoms/Icon';
import { useAnalyticsContext } from 'components/04-templates/AnalyticsProvider';

const backgroundColors: { [color: string]: string } = {
  dark: '#333333',
  light: 'white',
  outline: ''
};

const textColors: { [color: string]: string } = {
  light: 'black',
  dark: 'white',
  outline: 'white'
};

const iconFill: { [color: string]: string } = {
  light: '#333333',
  dark: 'black',
  outline: '#333333'
};

export type CallToActionProps = {
  /** The header  */
  heading: JSX.Element;
  /** The dek text */
  dek: string;
  /** There are 3 styled variants, dark and light and dark-outline */
  color: 'dark' | 'light' | 'outline';
  /** Temporary linkConfig. Will be replaced by a button component */
  linkConfig: LinkConfig;
  /** Price duration field */
  priceDuration?: string;
  /** Discount code */
  discountCode?: string;
  /** Price discount */
  discountPrice?: string;
  /** Price field */
  price?: string;
  /** Styles */
  classes?: string;
  /** Styles to be applied to the button component */
  buttonClasses?: string;
  /** Theme to be applied to the button: primary, secondary, or alert */
  buttonTheme?: 'primary' | 'secondary' | 'alert';
  /** Display the component wide or narrow */
  isFullWidth?: boolean;
  /** Fallback for old price disclaimer text */
  priceDisclaimer?: string;
};

export const CallToAction = ({
  heading,
  dek,
  color,
  linkConfig,
  price,
  priceDuration,
  discountPrice,
  discountCode,
  priceDisclaimer,
  isFullWidth = false,
  buttonClasses = '',
  buttonTheme = 'primary',
  classes = ''
}: CallToActionProps) => {
  const { elementClicked } = useAnalyticsContext();
  const textColor = textColors[color];
  const backgroundStyles = {
    backgroundColor: backgroundColors[color]
  };

  const handleOnClick: MouseEventHandler<HTMLAnchorElement> = (e) => {
    if (linkConfig.onClick) {
      linkConfig.onClick(e);
    }
    elementClicked({ name: 'in-content-cta', url: linkConfig.href });
  };

  const containerClasses = isFullWidth
    ? 'wide:flex wide:gap-12 items-center px-5 py-7 wide:px-12'
    : 'p-4 px-6';

  return (
    <div
      style={backgroundStyles}
      className={`${classes} ${containerClasses} relative overflow-hidden rounded-theme-border-default ${
        color === 'outline' ? 'border border-network-black-soft' : ''
      } w-full`.trim()}
    >
      <div className="absolute -bottom-4 -right-16">
        <Icon
          type="outsideO"
          color={iconFill[color]}
          classes={`relative z-100 ${isFullWidth ? 'w-48 h-48' : 'w-56 h-56'}`}
        />
      </div>

      <div className={`z-100 ${isFullWidth ? 'w-full wide:w-2/3' : ''}`}>
        <div className="z-10 mb-4 w-full justify-start">{heading}</div>
        <p
          className={`text-network-${textColor} z-100 relative pt-1 font-network-font-1 font-light`}
        >
          {dek}
        </p>
      </div>

      <div
        className={`z-100 relative ${
          isFullWidth ? 'text-center narrow-x:w-1/3' : ''
        }`}
      >
        <div className="inline-block w-full wide:w-auto">
          <Button
            classes={`${buttonClasses} block w-full wide:w-auto mb-2 px-8 wide:px-12`}
            text={linkConfig.text}
            href={linkConfig.href}
            size="md"
            theme={buttonTheme}
            onClick={handleOnClick}
          />

          <div
            className={`text-network-${textColor} z-10 space-x-1 text-right font-network-font-1 text-12 font-light`}
          >
            {discountCode && (
              <>
                <span>Sale</span>
                <span className="line-through">{price}</span>
                <span className="text-[#EA2127]">{discountPrice}</span>
                <span>{priceDuration}</span>
              </>
            )}
            {!discountCode && price && (
              <>
                Only {price} {priceDuration}
              </>
            )}
            {!discountCode && !price && priceDisclaimer}
          </div>
        </div>
      </div>
    </div>
  );
};
